import React, { ReactElement, useCallback, useEffect, useState, useRef, useContext } from 'react'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { FiLogOut, FiSearch, FiDelete } from 'react-icons/fi'
import InfiniteScroll from 'react-infinite-scroll-component'
import { productService } from '../../services/product.service'
import { Product as IProduct, ProductWithPagination } from '../../interfaces/definitions/product.interface'
import { Product } from '../Product/Product'
import companyLogo from '../../assets/jjaavation.png'
import './Product-list.css'
import { useInView } from 'react-intersection-observer'
import { useProductList } from '../../context/ProductListContext'
import { Bounce } from 'react-activity'
import { Link } from 'react-router-dom'
import { PermissionContext } from '../../context/PermissionContext'
import { MdOutlineAdminPanelSettings } from 'react-icons/md'
// import fetchWorkOrders from '../../services/smart145.service'

const InViewProduct = ({ product, scrollableListRef }: { product: IProduct, scrollableListRef: React.RefObject<HTMLDivElement> }): ReactElement | null => {
  const { ref, inView } = useInView({
    root: scrollableListRef.current,
    rootMargin: '500px'

  })
  return (
    <div ref={ref}>
      {inView ? <Product product={product} /> : <h6 className='list-status'> </h6>}
    </div>
  )
}

const ProductList: React.FC = () => {
  const { signOut } = useAuthenticator((context) => [context.signOut])

  const {
    products,
    setProducts,
    lastKey,
    setLastKey,
    hasMore,
    setHasMore,
    search,
    setSearch,
    isSearching,
    setIsSearching,
    searchHistory,
    setSearchHistory,
    addSearchToHistory
  } = useProductList()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedPrefix, setSelectedPrefix] = useState<string>('WO')
  const [error, setError] = useState<string>('')
  const [load, setLoad] = useState<boolean>(false)
  const scrollableListRef = useRef<HTMLDivElement>(null)
  const [showHistory, setShowHistory] = useState<boolean>(false)
  const { admin } = useContext(PermissionContext)

  const loadItems = useCallback(async (startKey?: string): Promise<void> => {
    try {
      const { items, nextStartKey } = (await productService.getProducts({
        mode: 'all',
        search: (search !== undefined && search !== '') ? selectedPrefix + '-' + search : undefined,
        startKey
      })) as ProductWithPagination

      setProducts((prevProducts) => {
        // Create a Set with existing product IDs for quick lookup
        const existingProductIds = new Set(prevProducts.map((product) => product.id))

        // Filter out duplicates by checking against existing IDs
        const uniqueItems = items.filter((item) => !existingProductIds.has(item.id))

        // Return new list with old products and unique new products
        return isSearching ? uniqueItems : [...prevProducts, ...uniqueItems]
      })

      setLastKey(nextStartKey)
      setHasMore(nextStartKey !== undefined)
    } catch (e) {
      console.log(e)
    } finally {
      setIsLoading(false)
      setIsSearching(false)
    }
  }, [search])

  const fetchMoreData = async (): Promise<void> => {
    if (!isLoading && lastKey !== null) {
      await loadItems(lastKey)
    }
  }

  const handleSearch = (): void => {
    if (search === '' || search === undefined) return
    addSearchToHistory(search)
    setSearchHistory((prevHistory) => {
      const newHistory = [search, ...prevHistory.filter(item => item !== search)]
      return newHistory.slice(0, 10)
    })
    setIsSearching(true)
    setProducts([])
    setLastKey(undefined)
    setLoad(!load)
  }

  const resetSearch = (): void => {
    setSearch('')
    setLastKey(undefined)
    setIsSearching(false)
    setProducts([])
    setLoad(!load)
  }

  useEffect(() => {
    setIsLoading(true)
    loadItems().catch(error => setError(error.message))
  }, [load])

  useEffect(() => {

    // const workOrder = async (): Promise<void> => { await fetchWorkOrders().then((data) => console.log(data)) }
    // workOrder().catch(error => setError(error.message))
  }, [])

  return (
    <div className='product-list-container'>
      <div className='header'>
        <div className='logo-container'>
          <img src={companyLogo} alt='Company Logo' className='company-logo' />
        </div>
        <div className='search-container'>
          <select
            value={selectedPrefix}
            onChange={(e) => setSelectedPrefix(e.target.value)}
            className='custom-select'
          >
            <option value='WO'>WO</option>
            <option value='RO'>RO</option>
            <option value='SO'>SO</option>
            <option value='RMA'>RMA</option>

          </select>
          <div className='search-history-container'>
            <input
              onFocus={() => setShowHistory(true)}
              onBlur={() => setTimeout(() => setShowHistory(false), 200)} // Delay hiding to allow click event on history items              type='text'
              placeholder='Search products...'
              className='search-input'
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
                if (e.key === 'Escape') {
                  resetSearch()
                }
              }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {showHistory && searchHistory.length > 0 && (
              <div className='search-history-list'>
                {searchHistory.map((item, index) => (
                  <div
                    key={index}
                    className='search-history-item'
                    onClick={() => {
                      setSearch(item)
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className='search-button' onClick={handleSearch}>
            <FiSearch />
          </button>
          <button className='reset-button' onClick={resetSearch}>
            <FiDelete />
          </button>
        </div>
        <button className='logout-button' onClick={signOut}>
          <FiLogOut />
        </button>
        {admin === true &&
          <Link to='/admin'>
            <button className='admin-button'>
              <MdOutlineAdminPanelSettings color='white' />
            </button>
          </Link>}
      </div>
      <div className='product-list'>
        <div className='list-header'>
          <span className='header-item'>Order Number</span>
          <span className='header-item'>Current Step</span>
        </div>
        <div style={{ scrollbarWidth: 'none' }} ref={scrollableListRef} className='scrollable-list' id='scrollableList'>
          <div>
            {error !== '' && <p className='list-status'>{error}</p>}
            {!isLoading && products.length === 0 && <p className='list-status'>No items found.</p>}
            {isLoading && <Bounce color='#4285f4' size={50} className='list-loader' />}
          </div>
          {
            !isLoading && products.length > 0 && (
              <InfiniteScroll
                dataLength={products.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={<Bounce color='#4285f4' size={50} className='list-status' />}
                scrollableTarget='scrollableList'
                scrollThreshold={0.75}
              >
                {products.map((product) => (
                  <InViewProduct key={product.id} product={product} scrollableListRef={scrollableListRef} />
                ))}
                {!hasMore && <p className='list-status' style={{ position: 'relative' }}>Oops! You have reached the end.</p>}
              </InfiniteScroll>
            )
          }

        </div>
      </div>
    </div>
  )
}

export default ProductList
